import axios, { AxiosError } from 'axios';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Datablock, StandardPrompt, VariableText } from '../types/Datablock';
import { DiffData, StandardPromptPreview, VariableDataPromptPreview } from './DatablockPreview';
import { Checkbox } from '@mantine/core';

interface DatablockDetailedViewProps {
  projectKey: string;
  environmentKey: string;
  datablockKey: string;
  version?: number;
}

export const DatablockDetailedView = ({
  projectKey,
  environmentKey,
  datablockKey,
  version,
}: DatablockDetailedViewProps) => {
  let [datablockListMessage, setDatablockListMessage] = useState<string>('');
  let [showComparison, setShowComparison] = useState<boolean>(false);
  const [currentDatablock, setCurrentDatablock] = useState<Datablock>();

  const {
    error,
    data: datablockData,
    refetch: refetchDatablockData,
  } = useQuery({
    queryKey: [`datablock-${projectKey}-${environmentKey}-${version}`],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken && version) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/project/${projectKey}/environment/${environmentKey}/datablock/${datablockKey}/${version}?includeCurrent=true`,
            {
              headers: headers,
            }
          );
          const datablocks: Datablock[] = response.data.datablocks;
          let queriedDatablock: Datablock | undefined;
          datablocks.map((datablock) => {
            if (datablock.version == version) {
              queriedDatablock = datablock;
            }
            if (datablock.status == 'CURRENT') {
              setCurrentDatablock(datablock);
            }
          });
          return queriedDatablock;
        } else {
          return null;
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setDatablockListMessage('Unable to fetch datablock details');
            return null;
          } else {
            setDatablockListMessage('A network error occured. Please try again later.');
          }
        } else {
          setDatablockListMessage('An error occured. Please try again later.');
          return null;
        }
      }
    },
  });

  const renderPreview = (datablock: Datablock) => {
    switch (datablock.type) {
      case 'STANDARD_PROMPT':
        let diffData: DiffData | undefined;
        if (currentDatablock && showComparison) {
          diffData = {
            showDiff: true,
            firstData: (datablock.data as StandardPrompt).prompt,
            secondData: (currentDatablock.data as StandardPrompt).prompt,
          };
        }
        return (
          <StandardPromptPreview
            key={datablock.key}
            name={datablock.name}
            envKey="A" //TODO: Fix this
            datablockKey={datablock.key}
            role={(datablock.data as StandardPrompt).role}
            prompt={(datablock.data as StandardPrompt).prompt}
            diffData={diffData}
          />
        );
      case 'VARIABLE_DATA':
        return (
          <VariableDataPromptPreview
            key={datablock.key}
            name={datablock.name}
            envKey={'A'} //TODO: Fix this
            datablockKey={datablock.key}
            variable={(datablock.data as VariableText).variable}
          />
        );
      default:
        return null;
    }
  };

  function handleCompareSelection() {
    setShowComparison(!showComparison);
  }

  return (
    <div className="pb-5">
      {version && datablockData ? (
        <div>
          {renderPreview(datablockData)}
          <div className="ml-8 mt-5">
            <Checkbox
              label="Compare this version with the latest version"
              onClick={() => handleCompareSelection()}
            />
          </div>
        </div>
      ) : (
        <div>Loading datablock details</div>
      )}
    </div>
  );
};

export default DatablockDetailedView;
